import { motion } from 'framer-motion'
import { Crown, Sparkles, TrendingDown, TrendingUp, Users } from 'lucide-react'

import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import { cn } from '@/lib/utils'

const KPI_ICONS = {
  Revenue: TrendingUp,
  Appointments: Sparkles,
  Utilization: Users,
  'VIP guests': Crown,
}

const KPI_THEMES = {
  Revenue: {
    gradient: 'from-primary/12 via-primary/5 to-transparent',
    icon: 'bg-primary text-primary-foreground shadow-md shadow-primary/25',
    ring: 'text-primary',
  },
  Appointments: {
    gradient: 'from-violet-500/10 via-violet-500/5 to-transparent',
    icon: 'bg-violet-600 text-white shadow-md shadow-violet-500/25',
    ring: 'text-violet-600',
  },
  Utilization: {
    gradient: 'from-emerald-500/12 via-emerald-500/5 to-transparent',
    icon: 'bg-emerald-600 text-white shadow-md shadow-emerald-500/25',
    ring: 'text-emerald-600',
  },
  'VIP guests': {
    gradient: 'from-amber-500/12 via-amber-500/5 to-transparent',
    icon: 'bg-gradient-to-br from-gold to-[hsl(31_45%_48%)] text-white shadow-md shadow-gold/30',
    ring: 'text-gold',
  },
}

export function KpiGrid() {
  const { data } = useCommandDashboard()
  const kpis = Array.isArray(data?.kpis) ? data.kpis : []

  if (kpis.length === 0) {
    return (
      <section className="card-surface p-6 text-center text-sm text-muted-foreground">
        No KPI data for today yet.
      </section>
    )
  }

  return (
    <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Key performance indicators">
      {kpis.map((kpi, i) => {
        const Icon = KPI_ICONS[kpi.title] ?? TrendingUp
        const theme = KPI_THEMES[kpi.title] ?? KPI_THEMES.Revenue
        const trendUp = String(kpi.trend ?? '').includes('▲') || !String(kpi.trend ?? '').includes('▼')

        return (
          <motion.article
            key={kpi.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 + i * 0.05 }}
            className={cn(
              'card-surface relative overflow-hidden p-4 transition-all hover:-translate-y-0.5 hover:shadow-luxury lg:p-5',
              `bg-gradient-to-br ${theme.gradient}`,
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {kpi.title}
                </p>
                <p className="mt-2 text-2xl font-bold tabular-nums leading-none tracking-tight lg:text-[1.75rem]">
                  {kpi.value}
                </p>
              </div>
              <div
                className={cn(
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
                  theme.icon,
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={2.25} />
              </div>
            </div>

            <div className="mt-4 space-y-1">
              <p
                className={cn(
                  'flex items-center gap-1 text-xs font-semibold',
                  trendUp && kpi.title === 'Revenue' ? 'text-emerald-600' : 'text-foreground/80',
                )}
              >
                {kpi.title === 'Revenue' ? (
                  trendUp ? (
                    <TrendingUp className="h-3.5 w-3.5" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5 text-rose-600" />
                  )
                ) : null}
                {kpi.trend}
              </p>
              <p className="text-sm font-medium text-foreground/90">{kpi.context}</p>
              <p className="text-[11px] text-muted-foreground">{kpi.detail}</p>
            </div>

            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-muted/70">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${kpi.progress_pct ?? kpi.progress ?? 0}%` }}
                transition={{ duration: 0.7, delay: 0.1 + i * 0.05 }}
                className={cn('h-full rounded-full bg-current opacity-80', theme.ring)}
              />
            </div>
          </motion.article>
        )
      })}
    </section>
  )
}
